import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { isAxiosError } from 'axios';
import isEmail from 'validator/lib/isEmail';
import { useLocalizeMessage, Input, ModalWindow, Select, Spinner, H5, Text, useToastNotification } from 'libs.nucleus';
import { useBooleanState } from 'libs.react';
import { getEmailChangeInitialValues, getEmailChangeReasons, isSameEmail } from './participant_email_change.utils';
import { getApiErrorMessage, getApiInstance } from '../../helpers/api.helpers';
import { getParticipantNumber } from '../../helpers/participants.helpers';
import { useCurrentEnvironment } from '../../hooks/use_current_environment';
import { useForm } from '../../hooks/use_form';
/**
 * Confirmation flow for changing a registered participant's email address (SSP-4580). The
 * change is a single Axon call (AXONCONFIG-5268) that updates the account and the participant
 * record together; Axon refuses it while a consent is partially signed, and eConsent reissues
 * unsigned invitations to the new address. The refusal reason comes back in the fault and is
 * surfaced as-is so the site user sees what to do next.
 */
export const ParticipantEmailChangeModal = ({ isModalOpen, participant, onClose, }) => {
    const translate = useLocalizeMessage();
    const { environment } = useCurrentEnvironment();
    const { addNotification } = useToastNotification();
    const [isSaving, , , toggleIsSaving] = useBooleanState(false);
    const { form, isFieldtouched, onFieldChange, onSelectFieldChange, resetForm } = useForm({
        initialValues: getEmailChangeInitialValues(),
    });
    const newEmail = form.email.trim();
    const enteredParticipantNumber = form.participantNumber.trim();
    const errors = {
        participantNumber: participant.c_number && !enteredParticipantNumber
            ? translate('validation.participant_email_change.id_required')
            : undefined,
        email: !newEmail
            ? translate('validation.field_required')
            : !isEmail(newEmail)
                ? translate('validation.invalid_email')
                : isSameEmail(newEmail, participant.c_email)
                    ? translate('validation.participant_email_change.same_email')
                    : undefined,
        reason: !form.reason?.value ? translate('validation.participant_email_change.reason_required') : undefined,
    };
    // If there is a participant number and it does not match the form participant number, show an error
    if (participant.c_number && enteredParticipantNumber && participant.c_number !== enteredParticipantNumber) {
        errors.participantNumber = translate('validation.participant_email_change.id_mismatch');
    }
    const formHasErrors = Object.values(errors).some((error) => !!error);
    const onModalClose = () => {
        resetForm();
        onClose();
    };
    const changeParticipantEmail = async () => {
        if (!environment) {
            throw new Error('Environment not defined');
        }
        toggleIsSaving();
        const body = {
            c_public_user: participant._id,
            c_email: newEmail,
            c_change_reason_code: form.reason.value,
        };
        try {
            await getApiInstance(environment).post('routes/participant/update_email', body);
            addNotification({
                title: '',
                subtitle: translate('status.participant_email_change.success'),
                type: 'success',
            });
            onModalClose();
        }
        catch (e) {
            // The Axon version gate on the details row is the pre-check; cortex.notFound.route here is the runtime truth
            // that this org's Axon build has no update_email route, and deserves a plainer message. Keyed on the fault
            // code, not the status: the route itself answers 404 (axon.notFound.instanceNotFound) for a participant that
            // no longer resolves, and that reason must show as-is.
            const routeUnavailable = isAxiosError(e) && e.response?.status === 404 && e.response?.data?.errCode === 'cortex.notFound.route';
            // Otherwise the fault reason is the actionable message (e.g. complete or void a partially signed consent
            // first). preferReason: Cortex omits `faults` from a fault without nested faults, and without it the helper
            // would return the generic `message` ("Invalid Argument") instead.
            const message = routeUnavailable
                ? translate('error.participant_email_change.route_unavailable')
                : getApiErrorMessage(e, true);
            addNotification({
                title: '',
                subtitle: message ?? translate('error.generic'),
                type: 'error',
            });
        }
        finally {
            toggleIsSaving();
        }
    };
    return (_jsx(ModalWindow, { title: translate('label.participant_email_change.title', {
            participantNumber: getParticipantNumber(participant),
        }), isOpen: isModalOpen, closeWindow: onModalClose, footerSecondaryActionButtons: [{ label: translate('generic.cancel'), onClick: onModalClose }], footerPrimaryActionButton: {
            // the footer renders outside the spinner overlay, so isSaving must disable it to prevent a second POST
            disabled: formHasErrors || isSaving,
            label: translate('action.participant_email_change.change_email'),
            onClick: changeParticipantEmail,
            id: 'change-participant-email-btn',
            dataTestId: 'change-participant-email-btn',
        }, width: 'full', children: _jsxs("div", { className: 'flex flex-col h-full justify-between', children: [isSaving && _jsx(Spinner, { wrapper: 'full' }), _jsxs("div", { className: 'flex flex-col flex-grow items-center gap-6 justify-center', children: [_jsxs("section", { className: 'w-1/3', children: [_jsx(H5, { children: translate('prompt.participant_email_change.confirm') }), _jsx(Text, { className: 'text-sm', children: translate('info.participant_email_change.confirmation') })] }), _jsx("section", { className: 'w-1/3', children: _jsxs(Text, { className: 'text-sm', dataTestId: 'current-participant-email', children: [translate('label.participant_email_change.current_email'), ": ", participant.c_email] }) }), participant.c_number && (_jsx("section", { className: 'w-1/3', children: _jsx(Input, { dataTestId: 'participant-number', id: 'participantNumber', name: 'participantNumber', label: translate('label.participant_id'), onChange: onFieldChange, value: form.participantNumber, width: 'full', required: true, errorMessage: errors.participantNumber && isFieldtouched('participantNumber') ? errors.participantNumber : undefined, hasError: !!(errors.participantNumber && isFieldtouched('participantNumber')) }) })), _jsx("section", { className: 'w-1/3', children: _jsx(Input, { autoComplete: 'off', dataTestId: 'new-participant-email', id: 'email', name: 'email', label: translate('label.participant_email_change.new_email'), onChange: onFieldChange, value: form.email, width: 'full', required: true, errorMessage: errors.email && isFieldtouched('email') ? errors.email : undefined, hasError: !!(errors.email && isFieldtouched('email')) }) }), _jsx("section", { className: 'w-1/3', children: _jsx(Select, { required: true, dataTestId: 'email-change-reason-select', errorMessage: errors.reason && isFieldtouched('reason') ? errors.reason : undefined, hasError: !!(errors.reason && isFieldtouched('reason')), label: translate('label.participant_email_change.reason'), onChange: (value) => {
                                    onSelectFieldChange(value, 'reason');
                                }, options: getEmailChangeReasons(translate), value: form.reason, width: 'full' }) })] })] }) }));
};
